import React from 'react';
import { CircleDot, ImageIcon, Lock, SignalHigh, Tag, UserCheck } from 'lucide-react';
import { AppBadge, UserAvatar, cn } from '../ui';
import {
  authorRoleLabel,
  eventSentence,
  formatDateTime,
  isStaffRole,
  timeAgo,
} from './supportVocabulary';
import { getCommentAuthorName, getTicketCreatorName } from './supportIdentity';

const EVENT_ICONS = { status: CircleDot, priority: SignalHigh, category: Tag, assignedTo: UserCheck };

const URL_RE = /(https?:\/\/[^\s<>"')\]]+)/g;

/** Plain text with its line breaks kept and its http(s) links made clickable. */
function MessageText({ text }) {
  const parts = String(text || '').split(URL_RE);
  return (
    <div className="tw-whitespace-pre-wrap tw-break-words tw-text-sm tw-leading-relaxed tw-text-content">
      {parts.map((part, i) => (i % 2 === 1
        ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="tw-break-all tw-text-primary tw-underline tw-underline-offset-2 tw-focus-ring tw-rounded-sm">{part}</a>
        : <React.Fragment key={i}>{part}</React.Fragment>))}
    </div>
  );
}

function When({ at }) {
  if (!at) return null;
  return (
    <time dateTime={new Date(at).toISOString()} title={formatDateTime(at)} className="tw-whitespace-nowrap tw-text-xs tw-text-content-subtle">
      {timeAgo(at)}
    </time>
  );
}

/**
 * One message in the thread: the opening request, a reply, or an internal
 * note. Who wrote it decides how it sits — the requester's words on the plain
 * surface, support's on a faint brand wash, an internal note on the warning
 * tint with its lock — and each also says so in words.
 */
function Message({ name, role, at, text, internal, fromSupport, isYou, viewerIsStaff, children }) {
  const roleLabel = authorRoleLabel(role, viewerIsStaff);
  return (
    <li className="tw-relative tw-flex tw-gap-3 tw-pb-5 last:tw-pb-0">
      <span className="tw-relative tw-z-[1] tw-shrink-0">
        <UserAvatar name={name} size="sm" />
      </span>
      <article
        aria-label={internal ? `Internal note from ${name}` : `Message from ${name}`}
        className={cn(
          'tw-min-w-0 tw-flex-1 tw-overflow-hidden tw-rounded tw-border tw-border-solid',
          internal ? 'tw-border-warning/40 tw-bg-warning-soft'
            : fromSupport ? 'tw-border-primary/25 tw-bg-surface-raised'
              : 'tw-border-line tw-bg-surface-raised',
        )}
      >
        <header
          className={cn(
            'tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-border-0 tw-border-b tw-border-solid tw-px-4 tw-py-2',
            internal ? 'tw-border-warning/30'
              : fromSupport ? 'tw-border-primary/15 tw-bg-primary/[0.05]'
                : 'tw-border-line-soft tw-bg-surface-alt',
          )}
        >
          <span className="tw-text-[0.8125rem] tw-font-semibold tw-text-content">
            {name}{isYou && <span className="tw-font-normal tw-text-content-subtle"> (you)</span>}
          </span>
          {roleLabel && <AppBadge variant="outline" size="sm">{roleLabel}</AppBadge>}
          {internal && (
            <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-warning-fg">
              <Lock size={12} aria-hidden="true" /> Internal note · staff only
            </span>
          )}
          <span className="tw-ml-auto"><When at={at} /></span>
        </header>
        <div className="tw-px-4 tw-py-3">
          <MessageText text={text} />
          {children}
        </div>
      </article>
    </li>
  );
}

/** A recorded change, as a quiet line between the messages it came between. */
function EventLine({ entry }) {
  const Icon = EVENT_ICONS[entry.event?.field] || CircleDot;
  return (
    <li className="tw-relative tw-flex tw-items-center tw-gap-3 tw-pb-5 last:tw-pb-0">
      <span className="tw-relative tw-z-[1] tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center">
        <span className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-text-content-subtle">
          <Icon size={13} aria-hidden="true" />
        </span>
      </span>
      <p className="tw-m-0 tw-min-w-0 tw-text-[0.8125rem] tw-text-content-muted">
        <span className="tw-font-semibold tw-text-content">{getCommentAuthorName(entry)}</span>{' '}
        {eventSentence(entry)}
        <span aria-hidden="true"> · </span>
        <When at={entry.createdAt} />
      </p>
    </li>
  );
}

/**
 * TicketThread — the ticket as a conversation: the request itself first, then
 * every reply, note and change in the order it happened, on one rail.
 *
 * Props:
 *   ticket         — from GET /support/tickets/:id (already filtered to what
 *                    this viewer may read)
 *   viewerId       — the signed-in user's id, to mark their own messages
 *   viewerIsStaff  — whether role chips use staff titles
 *   onOpenAttachment — open the screenshot in the viewer
 */
const TicketThread = ({ ticket, viewerId, viewerIsStaff, onOpenAttachment }) => {
  const requesterId = String(ticket.createdBy || '');
  const requesterName = getTicketCreatorName(ticket);

  return (
    <ol className="tw-relative tw-m-0 tw-list-none tw-p-0" aria-label="Conversation">
      <span aria-hidden="true" className="tw-absolute tw-bottom-4 tw-left-4 tw-top-4 tw-w-px tw-bg-line" />

      <Message
        name={requesterName}
        role={ticket.role}
        at={ticket.createdAt}
        text={ticket.description}
        isYou={requesterId && requesterId === String(viewerId || '')}
        fromSupport={false}
        viewerIsStaff={viewerIsStaff}
      >
        {ticket.attachmentUrl && (
          <button
            type="button"
            onClick={onOpenAttachment}
            className="tw-group tw-mt-3 tw-block tw-cursor-zoom-in tw-overflow-hidden tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-alt tw-p-0 tw-focus-ring"
            aria-label="Open the attached screenshot"
          >
            <img
              src={ticket.attachmentUrl}
              alt=""
              className="tw-block tw-max-h-56 tw-max-w-full tw-object-contain tw-transition-opacity group-hover:tw-opacity-90"
            />
            <span className="tw-flex tw-items-center tw-gap-1.5 tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-px-3 tw-py-1.5 tw-text-xs tw-text-content-muted">
              <ImageIcon size={13} aria-hidden="true" /> Screenshot · click to enlarge
            </span>
          </button>
        )}
      </Message>

      {(ticket.comments || []).map((c, i) => (c.kind === 'event'
        ? <EventLine key={c._id || `e${i}`} entry={c} />
        : (
          <Message
            key={c._id || `c${i}`}
            name={getCommentAuthorName(c)}
            role={c.userRole}
            at={c.createdAt}
            text={c.text}
            internal={!!c.isInternal}
            fromSupport={String(c.userId || '') !== requesterId && isStaffRole(c.userRole)}
            isYou={!!viewerId && String(c.userId || '') === String(viewerId)}
            viewerIsStaff={viewerIsStaff}
          />
        )))}
    </ol>
  );
};

export default TicketThread;
